import { useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import {
  DriverLoadingPage,
  PhotosInputDS,
  Spacer,
  TextCapitalized,
  triggerToastDS,
} from "@qivia/ui";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import { ButtonDS } from "@qivia/ui/src/designSystem/components/Button";
import { Badge } from "@qivia/ui/src/designSystem/components/Tag/Badge";
import { TextAreaDS } from "@qivia/ui/src/designSystem/components/Inputs/TextAreaInput";
import { Page } from "../../components/Page";
import { ErrorPage } from "../../components/ErrorPage";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { selectDriver } from "../../qrCode/driverKnownSlice";
import { selectSessionStartOfUsage } from "../../qrCode/menuSlice";
import {
  selectGetInitialVehicleRestitutionBySessionStatus,
  selectInitialVehicleRestitution,
  selectValidateVehicleRestitutionStatus,
  selectVehicleRestitutionPictures,
  validateVehicleRestitutionAsync,
  vehicleSessionsSlice,
} from "../vehicleRestitution/vehicleSessionsSlice";
import "../../../i18n/i18n";

interface InitialRestitutionReviewStepProps {
  onCorrect: () => void;
}

export const InitialRestitutionReviewStep = (
  props: InitialRestitutionReviewStepProps,
) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const driver = useAppSelector(selectDriver);
  const initialRestitution = useAppSelector(selectInitialVehicleRestitution);
  const restitutionPictures = useAppSelector(selectVehicleRestitutionPictures);
  const validateStatus = useAppSelector(selectValidateVehicleRestitutionStatus);
  const getInitialRestitutionStatus = useAppSelector(
    selectGetInitialVehicleRestitutionBySessionStatus,
  );
  const sessionStartOfUsage = useAppSelector(selectSessionStartOfUsage);

  const onValidate = useCallback(() => {
    if (!driver || !initialRestitution) return;
    void dispatch(
      validateVehicleRestitutionAsync({
        restitution: initialRestitution.uuid,
        validatedBy: driver,
      }),
    );
  }, [dispatch, driver, initialRestitution]);

  useEffect(() => {
    if (validateStatus === "failed") {
      triggerToastDS(t("restitution.validate.error.text") || "", "ERROR");
      dispatch(
        vehicleSessionsSlice.actions.resetValidateVehicleRestitutionStatus(),
      );
    }
  }, [validateStatus, t, dispatch]);

  const stateBadge = (value: boolean) => (
    <Badge
      text={t(`restitution.summary.${value}`)}
      backgroundColor={
        value
          ? colors["colors/system/success/success_ultraLight"]
          : colors["colors/system/error/error_ultraLight"]
      }
      textColor={
        value
          ? colors["colors/system/success/success_normal"]
          : colors["colors/system/error/error_normal"]
      }
    />
  );

  const detailRow = (label: string, value: string) => (
    <StyledFlex>
      <StyledDetailLabel>
        <TextCapitalized>{label}</TextCapitalized>
      </StyledDetailLabel>
      <Spacer x={1} />
      <StyledDetailValue>
        <TextCapitalized>{value}</TextCapitalized>
      </StyledDetailValue>
    </StyledFlex>
  );

  const isLoading =
    (getInitialRestitutionStatus === "processing" &&
      sessionStartOfUsage !== null) ||
    validateStatus === "processing";

  if (isLoading) return <DriverLoadingPage />;

  if (!initialRestitution) return <ErrorPage />;

  return (
    <Page
      title={t("restitution.checkBeforeDeparture.title")}
      returnButton
      returnButtonAction={() => navigate(-1)}
      withoutHeaderSpacer
      helpButton
      bottomButtonBackgroundColor="white"
      withShadow
      bottomButton={
        <StyledColumn>
          <ButtonDS
            format="fill"
            buttonType="primary"
            text={t("restitution.checkBeforeDeparture.button.conform")}
            onClick={onValidate}
          />
          <Spacer y={0.5} />
          <ButtonDS
            format="fill"
            buttonType="secondary"
            text={t("restitution.checkBeforeDeparture.button.reportGap")}
            onClick={props.onCorrect}
          />
        </StyledColumn>
      }
    >
      <StyledColumn>
        <StyledDescription>
          <TextCapitalized>
            {t("restitution.checkBeforeDeparture.description")}
          </TextCapitalized>
        </StyledDescription>
        <Spacer y={1.5} />
        <StyledSeparator />
        <Spacer y={1.5} />
        {detailRow(
          t("restitution.checkBeforeDeparture.mileage.label"),
          `${initialRestitution.mileage} ${t("restitution.checkBeforeDeparture.mileage.unit")}`,
        )}
        <Spacer y={1} />
        <StyledRow>
          <StyledLabel>
            <TextCapitalized>
              {t("restitution.vehicleCondition.isCabinCleaned")}
            </TextCapitalized>
          </StyledLabel>
          {stateBadge(initialRestitution.isCabinCleaned)}
        </StyledRow>
        <Spacer y={1} />
        <StyledRow>
          <StyledLabel>
            <TextCapitalized>
              {t("restitution.vehicleCondition.isTrunkCleaned")}
            </TextCapitalized>
          </StyledLabel>
          {stateBadge(initialRestitution.isTrunkCleaned)}
        </StyledRow>
        <Spacer y={1} />
        <StyledLabel>
          <TextCapitalized>
            {t("restitution.vehicleCondition.observations.label")}
          </TextCapitalized>
        </StyledLabel>
        <Spacer y={0.5} />
        <StyledTextArea>
          <TextAreaDS
            value={initialRestitution.observations ?? ""}
            $update={() => undefined}
            isDisabled
          />
        </StyledTextArea>
        <Spacer y={1} />
        <StyledLabel>
          <TextCapitalized>
            {t("restitution.checkDeclared.photos.label")}
          </TextCapitalized>
        </StyledLabel>
        <Spacer y={0.5} />
        {restitutionPictures && restitutionPictures.length > 0 ? (
          <PhotosInputDS
            newPictures={{ photos: [] }}
            oldPictures={{ photos: restitutionPictures }}
            withViewIcon
          />
        ) : (
          <StyledEmptyPhotos>
            <TextCapitalized>
              {t("restitution.checkDeclared.photos.empty")}
            </TextCapitalized>
          </StyledEmptyPhotos>
        )}
      </StyledColumn>
    </Page>
  );
};

const StyledColumn = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const StyledDescription = styled.div`
  ${typographies["Body/M"]}
  color: ${colors["colors/text/darkGrey"]};
`;

const StyledEmptyPhotos = styled.div`
  ${typographies["Body/M"]}
  color: ${colors["colors/text/lightGrey"]};
`;

const StyledFlex = styled.div`
  display: flex;
`;

const StyledDetailLabel = styled.div`
  ${typographies["Body/L"]}
  flex-shrink: 0;
`;

const StyledDetailValue = styled.div`
  flex: 1;
  text-align: right;
`;

const StyledSeparator = styled.div`
  width: 100%;
  border-bottom: 0.0625rem solid ${colors["colors/borders/separator/cells"]};
`;

const StyledRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const StyledLabel = styled.div`
  ${typographies["Body/L"]}
`;

const StyledTextArea = styled.div`
  width: 100%;
`;
